import { LANGUAGES } from "@/const/language.constants"
import { SEVERITIES_FILTER_OPTIONS } from "@/const/options.constants"
import { WORKLOAD_STATUS } from "@/domain/application"
import { RANGE_DELIMITER_QUERY_STRING } from "@/organisms/SmartSearch/const/date.constants"
import { INEQUALITY_SYMBOL } from "@/organisms/SmartSearch/const/symbol.constants"

import type { TFunction } from "@/hooks/useTranslation"
import type {
  Option,
  OptionFilter
} from "@/organisms/SmartSearch/types/options.types"

type GetFilter<R = OptionFilter> = (
  key: string,
  t: TFunction,
  props?: { label?: string; options?: Option[] }
) => R

export const PAGE_NUMBER = "pageNumber"
export const QUERY = "query"
export const DAYS_AGO = "daysAgo"
export const SEEN_TIMESTAMP = "seenTimestamp"
export const ORDER_BY = "orderBy"
export const ASCENDING = "ascending"
export const YES = "YES"
export const KEY_PREFIX = "~"

export const removeCommonParamsQueryString = (
  search: string = window.location.search
) => {
  const params = new URLSearchParams(search)

  params.delete(PAGE_NUMBER)
  params.delete(ORDER_BY)
  params.delete(ASCENDING)

  return params.toString()
}

export const RANGE_DATE_PICKER_FILTER = {
  from: "fromDate",
  to: "toDate"
}

export const API_FILTER_OPTION_PLACEHOLDER = "apiFilterOptionPlaceholder"

export const formatLabelWithQuotesIfNeeded = (value: string) =>
  /\s/.test(value) ? `"${value}"` : value

export const getYesNoOptions = (t: TFunction): Option[] => [
  { label: t("general.yes"), value: YES },
  { label: t("general.no"), value: "NO" }
]

export const getYesNoNAOptions = (t: TFunction): Option[] => [
  ...getYesNoOptions(t),
  { label: "N/A", value: "UNDETERMINED", disabled: true }
]

export const getYesNoUndeterminedOptions = (t: TFunction): Option[] => [
  { label: t("general.yes"), value: "YES" },
  { label: t("general.no"), value: "NO" },
  { label: t("general.undetermined"), value: "UNDETERMINED" }
]

export const helperKeyMultipleValues = "filters.helpers.multipleValues"

export const getLabel: GetFilter<string> = (key, t, props) =>
  t(props?.label || `filters.${key}`)

export const getInputFilter: GetFilter = (key, t, props) => {
  const label = getLabel(key, t, props)

  return {
    key,
    label,
    type: "input",
    helper: [
      {
        label: t(helperKeyMultipleValues, { label }),
        description: t("filters.helpers.multipleSubstring", { label })
      }
    ]
  }
}

export const getMultipleValuesFilter: GetFilter = (key, t, props) => {
  const label = getLabel(key, t, props)

  return {
    key,
    label,
    options: props?.options || [],
    type: "multiple",
    helper: [
      {
        label: t(helperKeyMultipleValues, { label }),
        description: t("filters.helpers.equals")
      }
    ]
  }
}

export const getIssueTypeFilter = (t: TFunction): OptionFilter => ({
  key: "issueType",
  label: t("filters.issueType"),
  type: "multiple",
  helper: [
    {
      label: "issue-type:value1,value2",
      description: "Equals value1 OR value2"
    }
  ],
  options: []
})

export const getDismissReasonFilter = (t: TFunction): OptionFilter => ({
  key: "dismissReason",
  label: t("filters.dismissReason"),
  type: "input",
  helper: [
    {
      label: "Dismiss-Reason: value1",
      description:
        'Matches Dismiss Reason text as a substring (e.g. "mitigated by")'
    }
  ]
})

export const getDismissedByFilter = (t: TFunction): OptionFilter => ({
  key: "dismissedBy",
  label: t("filters.dismissedBy"),
  type: "input",
  helper: [
    {
      label: "Dismissed-By: value",
      description: 'Matches who dismissed the issue exactly (e.g. "Kodem")'
    }
  ]
})

export const getLanguagesFilter = (t: TFunction): OptionFilter => ({
  key: "language",
  label: t("filters.languages"),
  type: "multiple",
  options: getLanguageFilterOptions(),
  helper: [
    {
      label: t("language:value1,value2", { label: t("filters.languages") }),
      description: "Equals value1 OR value2"
    }
  ]
})

export const getHasFunctionEvidenceFilter = (t: TFunction): OptionFilter => ({
  key: "runtimeFunctionExecuted",
  label: t("filters.runtimeFunctionExecuted"),
  type: "single",
  options: getYesNoUndeterminedOptions(t)
})

export const getOsLanguagesFilter = (t: TFunction): OptionFilter => ({
  key: "osLanguages",
  label: t("filters.osLanguages").toLowerCase(),
  displayLabel: t("filters.osLanguages"),
  type: "single",
  options: getYesNoOptions(t)
})

export const getLabelsFilter = (t: TFunction): OptionFilter => ({
  key: "label",
  label: t("filters.labels"),
  linkedKey: "label",
  condition: "AND",
  type: "multiple",
  helper: [
    {
      label: t(helperKeyMultipleValues, {
        label: t("filters.labels")
      }),
      description: "Equals value1 OR value2"
    }
  ],
  options: []
})

export const getNotLabelsFilter = (t: TFunction): OptionFilter => ({
  key: "notLabel",
  label: `${INEQUALITY_SYMBOL}${t("filters.labels")}`,
  type: "multiple",
  linkedKey: "label",
  inequality: true,
  helper: [
    {
      label: `${INEQUALITY_SYMBOL}${t(helperKeyMultipleValues, {
        label: t("filters.labels")
      })}`,
      description: "Not equals value1 OR value2"
    }
  ],
  options: []
})

export const getJiraIssueIdFilter = (t: TFunction): OptionFilter => ({
  key: "jiraIssueId",
  label: t("filters.jiraIssueId").toLowerCase(),
  displayLabel: t("filters.jiraIssueId"),
  type: "input",
  helper: [
    {
      label: t(helperKeyMultipleValues, {
        label: t("filters.jiraIssueId")
      }),
      description: "Matches external IDs as substring (e.g. kod-11, kod23)"
    }
  ]
})

export const getOsFilter = (t: TFunction): OptionFilter => ({
  key: "os",
  label: t("filters.os").toLowerCase(),
  displayLabel: t("filters.os"),
  type: "input",
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.os") }),
      description: t("filters.helpers.multipleSubstring", {
        label: "operating systems"
      })
    }
  ]
})

export const getIsDevDependencyFilter = (t: TFunction): OptionFilter => ({
  key: "isDevDependency",
  label: t("filters.isDevDependency"),
  type: "multiple",
  order: "none",
  options: getYesNoUndeterminedOptions(t),
  helper: [
    {
      label: t(helperKeyMultipleValues, {
        label: t("filters.isDevDependency")
      }),
      description: t("filters.helpers.devDependency")
    }
  ]
})

export const getRuleIdFilter = (t: TFunction): OptionFilter => ({
  key: "ruleId",
  label: t("filters.ruleId").toLowerCase(),
  displayLabel: t("filters.ruleId"),
  type: "input",
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.ruleId") }),
      description:
        'Matches multiple rule-IDs (e.g. "python_exec_rule-subprocess-popen-shell-true")'
    }
  ]
})

export const getFixVersionFilter = (t: TFunction): OptionFilter => ({
  key: "fixVersion",
  label: t("filters.fixVersion"),
  type: "single",
  options: getYesNoOptions(t)
})

export const getKodemRemediationFilter = (t: TFunction): OptionFilter => ({
  key: "kodemRemediation",
  label: t("filters.kodemRemediation"),
  type: "single",
  options: getYesNoOptions(t)
})

export const getHighProfileFilter = (t: TFunction): OptionFilter => ({
  key: "highProfile",
  label: t("filters.highProfile"),
  type: "single",
  options: getYesNoOptions(t)
})

export const getRemediationEffortFilter = (t: TFunction): OptionFilter => ({
  key: "remediationEffort",
  label: t("filters.remediationEffort"),
  type: "multiple",
  order: "none",
  options: []
})

export const getFixableFilter = (t: TFunction): OptionFilter => ({
  key: "fixable",
  label: t("filters.fixable"),
  type: "single",
  frequent: true,
  frequentOrder: 4,
  options: getYesNoOptions(t),
  helper: [
    {
      label: `${t("filters.fixable")}: ${t("general.yes")}`,
      description: `Matches issues with either ${t("filters.fixVersion")} OR ${t("filters.kodemRemediation")}`
    }
  ]
})

export const getResolvedInScopeFilter = (t: TFunction): OptionFilter => ({
  key: "resolvedInScope",
  label: t("filters.resolvedInScope"),
  type: "single",
  options: getYesNoOptions(t)
})

export const buildQueryStringFromRecord = (
  params: Record<string, any>
): string => {
  const query = new URLSearchParams(params)

  return query.toString()
}

export const getLanguageFilterOptions = () =>
  LANGUAGES.map((value) => ({ label: value, value }))

export const getIssueStatusFilter = (t: TFunction): OptionFilter => ({
  key: "issueStatus",
  label: t("filters.issueStatus"),
  type: "multiple",
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.issueStatus") }),
      description: t("filters.helpers.equals")
    }
  ],
  options: [
    { label: t("general.open"), value: "open" },
    { label: t("general.resolved"), value: "resolved" },
    {
      label: t("general.resolvedInCurrentScope"),
      value: "resolvedInCurrentScope"
    },
    { label: t("general.dismissed"), value: "dismissed" }
  ]
})

export const getKaiInsightFilter = (t: TFunction): OptionFilter => ({
  key: "kaiInsight",
  label: t("filters.kaiInsight"),
  type: "single",
  frequent: true,
  frequentOrder: 3,
  options: [
    { label: "Confirmed", value: "Confirmed" },
    { label: "False Positive", value: "False Positive" },
    { label: "Undetermined", value: "Undetermined" }
  ]
})

export const getKaiDataFlowFilter = (t: TFunction): OptionFilter => ({
  key: "kaiDataFlow",
  label: t("filters.kaiDataFlow"),
  type: "single",
  options: [
    { label: "Yes", value: "YES" },
    { label: "No", value: "NO" }
  ]
})

export const getTimeRangeFilter = (t: TFunction, tKey: string) => {
  const key = t(`filters.${tKey}`)

  return {
    key,
    type: "time-range" as const,
    label: key
  }
}

export const RISK_ID_FILTER_KEY = "riskId"

export const getVulnerabilityIdFilter = (
  t: TFunction,
  type?: OptionFilter["type"]
) => {
  const key = RISK_ID_FILTER_KEY
  const displayLabel = t(`filters.${key}`)
  const filterType = type ?? "input"

  return {
    key,
    displayLabel,
    frequent: true,
    frequentOrder: 2,
    label: displayLabel.toLowerCase(),
    type: filterType,
    ...(filterType === "multiple" && {
      options: [getShouldFetchFilterOption()]
    }),
    trimValuesOnPaste: true,
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: displayLabel }),
        description:
          "Matches vulnerability identifiers by substring (e.g. CVE-2023-44487, GHSA-jq63-j4m2-6w3c, MAL-2022-0001)"
      }
    ]
  } as OptionFilter
}

export const getCveIdFilter = (t: TFunction) => {
  const label = t("filters.cve")
  const displayLabel = label.toUpperCase()

  return {
    label,
    displayLabel,
    key: `${RISK_ID_FILTER_KEY}_cve`,
    groupKey: RISK_ID_FILTER_KEY,
    trimValuesOnPaste: true,
    type: "input",
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: displayLabel }),
        description:
          "Matches multiple cve or malicious identifiers as a substring (e.g. CVE-2022-3715, MAL-2022-4691)"
      }
    ]
  } as OptionFilter
}

export const getBaseImageNameFilter = (t: TFunction): OptionFilter => ({
  key: "baseImageName",
  label: t("filters.baseImageName"),
  type: "input",
  helper: [
    {
      label: t(helperKeyMultipleValues, { label: t("filters.baseImageName") }),
      description: t("inventory.generalFilters.multipleSubstring", {
        name: "base images"
      })
    }
  ]
})

export const getBaseImageDigestFilter = (t: TFunction): OptionFilter => ({
  key: "baseImageDigest",
  label: t("filters.baseImageDigest"),
  type: "input",
  helper: [
    {
      label: t(helperKeyMultipleValues, {
        label: t("filters.baseImageDigest")
      }),
      description: "Matches base image digest exactly"
    }
  ]
})

export const getResolvedTime = (t: TFunction): OptionFilter =>
  getTimeRangeFilter(t, "resolvedTime")

export const getDiscoveredTime = (t: TFunction): OptionFilter =>
  getTimeRangeFilter(t, "discoveredTime")

export const getDismissedTime = (t: TFunction): OptionFilter =>
  getTimeRangeFilter(t, "dismissedTime")

export const getTimeRangeFilterValueByKey = <P extends Record<string, unknown>>(
  params: P,
  key: string,
  nextKey: string = key.replace(/-([a-z])/g, (_, letter) =>
    letter.toUpperCase()
  )
): P => {
  try {
    const { [key]: currentValue, ...rest } = params

    if (!currentValue) return params

    const values = `${currentValue}`
      .split(RANGE_DELIMITER_QUERY_STRING)
      .map((v) => v.trim())

    const from = Number(values[0])
    const to = Number(values[1])

    if (!from || !to) return params

    return {
      [`${nextKey}From`]: `${from}`,
      [`${nextKey}To`]: `${to}`,
      ...rest
    } as P
  } catch {
    return params
  }
}

export const TIME_FILTERS = {
  RESOLVED: "resolved-time",
  DISMISSED: "dismissed-time",
  DISCOVERED: "discovered-time"
}

export const getTimeRangeFiltersValues = <P extends Record<string, unknown>>(
  params: P
) =>
  [
    TIME_FILTERS.RESOLVED,
    TIME_FILTERS.DISMISSED,
    TIME_FILTERS.DISCOVERED
  ].reduce((acc, key) => getTimeRangeFilterValueByKey(acc, key), params)

export const getPackageLicenseFilter = (t: TFunction): OptionFilter => {
  return {
    key: "packageLicense",
    label: t("filters.license"),
    type: "multiple",
    options: [getShouldFetchFilterOption()],
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.license") }),
        description: "Matches multiple licenses as substring"
      }
    ]
  }
}

export const getShouldFetchFilterOption = (): Option => {
  return {
    label: "",
    value: API_FILTER_OPTION_PLACEHOLDER
  }
}

export const hasShouldFetchFilterOption = (
  filterOptions?: Option[]
): boolean => {
  return (
    Array.isArray(filterOptions) &&
    filterOptions.some((f) => f.value === API_FILTER_OPTION_PLACEHOLDER)
  )
}

export const getPackageVersionFilter = (t: TFunction): OptionFilter => {
  return {
    key: "packageVersion",
    label: t("filters.version"),
    type: "input",
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t("filters.version")
        }),
        description: "Matches multiple versions as substring"
      }
    ]
  }
}

export const getPackageNameFilter = (t: TFunction): OptionFilter => {
  return {
    key: "packageName",
    label: t("filters.package"),
    type: "input",
    valuesInQuotes: true,
    helper: [
      {
        label: [
          "Examples:",
          "packagename",
          '"packagename 1.2.3"',
          '"packagename 1.2.*"',
          '"packagename 1.2.0-1.2.5" (numeric only)',
          '"packagename > 1.2.3" (numeric only)',
          '"packagename < 2.0.0" (numeric only)',
          '"packagename 0.0.12","packagename 0.0.12-0.0.40"'
        ],
        description:
          "Package name matches by substring. Supports exact versions, ranges,\nwildcards (*), and < / >. Ranges and comparisons require strictly numeric\nversions (X.Y.Z) - alphanumeric versions support exact match only."
      }
    ],
    frequent: true,
    frequentOrder: 1
  }
}

export const getCweIdFilter = (
  t: TFunction,
  type: Extract<OptionFilter["type"], "input" | "multiple"> = "input"
): OptionFilter => {
  return {
    type,
    key: "cweId",
    label: t("filters.cweId").toLowerCase(),
    displayLabel: t("filters.cweId"),
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t("filters.cweId")
        }),
        description: "Matches multiple cwe identifiers (e.g. cwe-506)"
      }
    ],
    ...(type === "multiple" ? { options: [getShouldFetchFilterOption()] } : {})
  }
}

export const getSeverityFilter = (
  t: TFunction,
  options?: Option[]
): OptionFilter => {
  return {
    key: "severity",
    label: t("filters.severity"),
    type: "multiple",
    options: options || SEVERITIES_FILTER_OPTIONS,
    helper: [
      {
        label: t(helperKeyMultipleValues, { label: t("filters.severity") }),
        description: t("filters.helpers.equals")
      }
    ],
    frequent: true,
    frequentOrder: 5
  }
}

export const getPackageCategoryAI = (t: TFunction): OptionFilter => {
  return {
    key: "aiPackageCategory",
    label: t("filters.aiPackageCategory").toLowerCase(),
    displayLabel: t("filters.aiPackageCategory"),
    type: "multiple",
    options: [
      "AI Inference",
      "AI Agents",
      "AI Training",
      "AI Guardrails",
      "AI Observability",
      "AI General"
    ].map((value) => ({ label: value, value })),
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t("filters.aiPackageCategory")
        }),
        description: t("filters.helpers.equals")
      }
    ]
  }
}

export const getIsPackageAiRelated = (
  t: TFunction,
  getOptions = getYesNoNAOptions
): OptionFilter => {
  return {
    key: "isPackageAiRelated",
    label: t("filters.isPackageAiRelated").toLowerCase(),
    displayLabel: t("filters.isPackageAiRelated"),
    type: "single",
    options: getOptions(t),
    helper: [
      {
        label: t(helperKeyMultipleValues, {
          label: t("filters.isPackageAiRelated")
        }),
        description: t("filters.helpers.equals")
      }
    ]
  }
}

export const getHasActionFilter = (t: TFunction): OptionFilter => ({
  key: "hasAction",
  label: t("filters.hasAction"),
  order: 4,
  type: "single",
  options: getYesNoOptions(t)
})

export const getResolvedInCodeRepoFilter = (t: TFunction): OptionFilter => ({
  key: "resolvedInCodeRepo",
  label: t("filters.resolvedInCodeRepo"),
  type: "single",
  options: getYesNoOptions(t)
})

export const APPLICATIONS_DEFAULT_QUERY_STRING = new URLSearchParams({
  [PAGE_NUMBER]: "0",
  workloadStatus: [WORKLOAD_STATUS.ACTIVE, WORKLOAD_STATUS.INACTIVE].join(",")
}).toString()

export const getApplications = (t: TFunction): OptionFilter => ({
  key: "applicationName",
  label: t("filters.application"),
  type: "input",
  helper: [
    {
      label: t("filters.helpers.application.label"),
      description: t("filters.helpers.application.description")
    }
  ]
})

// SmartSearch lowercases the typed key and finds its filter by the exact label,
// so the label is the lowercase key and the readable one is only displayed
export const withDisplayLabel = (filter: OptionFilter): OptionFilter => ({
  ...filter,
  label: filter.label.toLocaleLowerCase(),
  displayLabel: filter.label
})
